/**
 * Shared helpers for accommodation prepayment policy → UI copy.
 *
 * Maps accommodation-level default policy to user-facing expectation text.
 * The guest never chooses payment mode — this is informational only.
 */

export type PrepaymentPolicy = 'none' | 'optional' | 'required';
export type PrepaymentType = 'fixed' | 'percentage';

/**
 * Resolve prepayment type from DB value, defaulting to 'fixed' for backward compat.
 */
export function resolvePrepaymentType(raw: string | null | undefined): PrepaymentType {
  if (raw === 'percentage') return 'percentage';
  return 'fixed';
}

/**
 * Compute the prepayment amount in öre based on type.
 * - fixed: returns fixedAmountOre directly
 * - percentage: returns totalStayOre * (percentage / 100), rounded
 */
export function computePrepaymentAmountOre(
  type: PrepaymentType,
  fixedAmountOre: number | null | undefined,
  percentage: number | null | undefined,
  totalStayOre: number | null | undefined,
): number | null {
  if (type === 'percentage') {
    if (percentage == null || percentage <= 0 || totalStayOre == null || totalStayOre <= 0) return null;
    return Math.round(totalStayOre * (percentage / 100));
  }
  return fixedAmountOre ?? null;
}

/**
 * Resolve raw DB value to typed policy, defaulting to 'none'.
 */
export function resolvePrepaymentPolicy(raw: string | null | undefined): PrepaymentPolicy {
  if (raw === 'optional' || raw === 'required') return raw;
  return 'none';
}

/**
 * Get the guest-facing trust/expectation text for the booking form,
 * based on the accommodation's default prepayment policy.
 */
export function getPrepaymentExpectationText(policy: PrepaymentPolicy): string {
  switch (policy) {
    case 'required':
      return 'För denna vistelse krävs vanligtvis en förskottsbetalning efter bekräftelse';
    case 'optional':
      return 'För denna vistelse kan en förskottsbetalning begäras efter bekräftelse';
    case 'none':
    default:
      return 'Betalning sker inte i samband med bokningen';
  }
}

/**
 * Short variant for tight spaces (e.g. mobile sticky bar).
 */
export function getPrepaymentExpectationShort(policy: PrepaymentPolicy): string {
  switch (policy) {
    case 'required':
      return 'Förskottsbetalning krävs';
    case 'optional':
      return 'Förskott kan begäras';
    case 'none':
    default:
      return 'Ingen betalning nu';
  }
}

/**
 * Success page trust text after booking submission.
 */
export function getPrepaymentSuccessText(policy: PrepaymentPolicy): string {
  switch (policy) {
    case 'required':
      return 'Värden kan begära en förskottsbetalning för att säkra bokningen';
    case 'optional':
      return 'Värden kan begära en förskottsbetalning för att säkra bokningen';
    case 'none':
    default:
      return 'Ingen betalning har gjorts – detta är en förfrågan';
  }
}
